<template>
  <div
    class="resize-handle"
    :class="{ 'is-resizing': isResizing }"
    @mousedown.prevent="startResize"
    @touchstart.prevent="startResize"
  >
    <div class="resize-indicator" />
  </div>
</template>

<script>
/**
 * ResizeHandle Component
 *
 * A reusable drag handle for resizing panels.
 * Emits 'resize' events with the new width during drag,
 * and 'resize-end' when dragging stops.
 *
 * @props {Number} minWidth - Minimum allowed width (default: 200)
 * @props {Number} maxWidth - Maximum allowed width (default: 500)
 * @props {Number} currentWidth - Current width of the resizable element
 *
 * @emits resize - Emitted during resize with new width value
 * @emits resize-end - Emitted when resize operation completes
 */
export default {
  name: 'ResizeHandle',

  props: {
    minWidth: {
      type: Number,
      default: 200,
    },
    maxWidth: {
      type: Number,
      default: 500,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isResizing: false,
      startX: 0,
      startWidth: 0,
    };
  },

  methods: {
    /**
     * Start resize operation
     */
    startResize(e) {
      this.isResizing = true;
      this.startX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
      this.startWidth = this.currentWidth;

      // Add document-level listeners
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      document.addEventListener('touchmove', this.onResize, { passive: false });
      document.addEventListener('touchend', this.stopResize);

      // Set cursor and prevent text selection
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';

      // Emit start event
      this.$emit('resize-start');
    },

    /**
     * Handle resize movement
     */
    onResize(e) {
      if (!this.isResizing) return;

      // Prevent default to avoid scrolling on touch
      if (e.type === 'touchmove') {
        e.preventDefault();
      }

      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const delta = clientX - this.startX;
      const newWidth = Math.max(
        this.minWidth,
        Math.min(this.maxWidth, this.startWidth + delta)
      );

      this.$emit('resize', newWidth);
    },

    /**
     * Stop resize operation
     */
    stopResize() {
      if (!this.isResizing) return;

      this.isResizing = false;

      // Remove document-level listeners
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.onResize);
      document.removeEventListener('touchend', this.stopResize);

      // Restore cursor and text selection
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      // Emit end event
      this.$emit('resize-end');
    },
  },

  beforeDestroy() {
    // Clean up any lingering listeners
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.stopResize);
    document.removeEventListener('touchmove', this.onResize);
    document.removeEventListener('touchend', this.stopResize);
  },
};
</script>

<style lang="scss" scoped>
.resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: var(--ds-nav-panel-resize-handle, 6px);
  height: 100%;
  cursor: col-resize;
  z-index: 10;

  // Larger hit area via pseudo-element
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -4px;
    right: -4px;
    height: 100%;
  }

  &:hover,
  &.is-resizing {
    .resize-indicator {
      opacity: 1;
      transform: translateX(-50%) scaleX(1);
    }
  }
}

.resize-indicator {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) scaleX(0.5);
  width: 3px;
  height: 100%;
  background: #48484857;
  opacity: 0;
  transition: opacity 150ms ease, transform 150ms ease;
  border-radius: 2px;
}

// When actively resizing, show a stronger indicator
.is-resizing .resize-indicator {
  width: 4px;
  opacity: 0.6;
  background: #48484887;
}
</style>
